import { useTranslation } from 'react-i18next'
import { useCacheHelpers } from '../apollo/cache-helpers'
import { useBoreholeFieldContainers } from '../borehole-field-container/use-borehole-field-containers'
import {
  BoreholeFieldContainerInput,
  CreateLocalGridInput,
  LocalGridFragment,
  LocalGridFragmentDoc,
  ProjectVersionFragment,
  ProviderFragmentDoc,
  UpdateLocalGridInput,
  useCreateLocalGridMutation,
  useDeleteLocalGridMutation,
  useUpdateLocalGridBoreholeFieldMutation,
  useUpdateLocalGridMutation,
} from '../generated/graphql'
import { useNotifications } from '../notifications/use-notifications'
import { localGridTypeToProviderTypeMap } from './local-grid-util'
import { useProviders } from '../provider/use-providers'

type LocalGridMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isUpdateBoreholeFieldLoading: boolean
  isDeleteLoading: boolean

  createLocalGrid: (
    projectVersion: ProjectVersionFragment,
    input: CreateLocalGridInput,
  ) => Promise<LocalGridFragment>

  updateLocalGrid: (
    id: string,
    input: UpdateLocalGridInput,
  ) => Promise<LocalGridFragment>

  updateLocalGridBoreholeField: (
    id: string,
    input: BoreholeFieldContainerInput,
  ) => Promise<LocalGridFragment>

  deleteLocalGrid: (
    projectVersion: ProjectVersionFragment,
    localGrid: LocalGridFragment,
    connectedGrid?: LocalGridFragment,
    connectedGridProviderId?: string,
  ) => Promise<boolean>
}

export function useLocalGrids(): LocalGridMutations {
  const [createLocalGrid, { loading: isCreateLoading }] =
    useCreateLocalGridMutation()
  const [updateLocalGrid, { loading: isUpdateLoading }] =
    useUpdateLocalGridMutation()
  const [
    updateLocalGridBoreholeField,
    { loading: isUpdateBoreholeFieldLoading },
  ] = useUpdateLocalGridBoreholeFieldMutation()
  const [deleteLocalGrid, { loading: isDeleteLoading }] =
    useDeleteLocalGridMutation()
  const { addItemToList, removeItemFromList } = useCacheHelpers()
  const { enqueueError } = useNotifications()
  const { t } = useTranslation()
  const { getContainerForUpdateOptimisticResponse } =
    useBoreholeFieldContainers()
  const { deleteProvider } = useProviders()

  return {
    isCreateLoading,
    isUpdateLoading,
    isUpdateBoreholeFieldLoading,
    isDeleteLoading,

    async createLocalGrid(
      projectVersion: ProjectVersionFragment,
      input: CreateLocalGridInput,
    ) {
      const result = await createLocalGrid({
        variables: {
          projectVersionId: projectVersion.id,
          input,
        },

        update: (_, mutationResult) => {
          const localGrid = mutationResult.data?.createLocalGrid
          if (!localGrid) {
            return
          }

          addItemToList(
            LocalGridFragmentDoc,
            localGrid,
            projectVersion,
            'localGrids',
          )
        },
      })

      if (!result.data) {
        throw new Error('Local grid creation failed')
      }

      return result.data.createLocalGrid
    },

    async updateLocalGrid(id: string, input: UpdateLocalGridInput) {
      const result = await updateLocalGrid({
        variables: { id, input },
      })

      if (!result.data) {
        throw new Error('Local grid update failed')
      }

      return result.data.updateLocalGrid
    },

    async updateLocalGridBoreholeField(
      id: string,
      input: BoreholeFieldContainerInput,
    ) {
      const result = await updateLocalGridBoreholeField({
        variables: { id, input },

        optimisticResponse: () => {
          const localGridFragment =
            getContainerForUpdateOptimisticResponse<LocalGridFragment>(
              id,
              input,
              LocalGridFragmentDoc,
            )

          return { updateLocalGridBoreholeField: localGridFragment }
        },
      })

      if (!result.data) {
        throw new Error('Local grid borehole field update failed')
      }

      return result.data.updateLocalGridBoreholeField
    },

    async deleteLocalGrid(
      projectVersion: ProjectVersionFragment,
      localGrid: LocalGridFragment,
      connectedGrid?: LocalGridFragment,
      connectedGridProviderId?: string,
    ) {
      if (connectedGrid && connectedGridProviderId) {
        await deleteProvider(undefined, connectedGrid, connectedGridProviderId)
      }

      const providerTypeToRemove =
        localGridTypeToProviderTypeMap[localGrid.type]

      const fallbackProvidersExist = projectVersion.buildings
        .flatMap((building) => building.providers)
        .filter((provider) => provider.type === providerTypeToRemove)
        .some((providerToRemove) => providerToRemove.isFallback)

      if (fallbackProvidersExist) {
        enqueueError(
          t(
            `localGrid.${localGrid.type}.deleteDialog.errorFallbackDevicesExist`,
          ),
        )

        return false
      }

      const result = await deleteLocalGrid({
        variables: { id: localGrid.id },

        update: () => {
          removeItemFromList(
            LocalGridFragmentDoc,
            localGrid.id,
            projectVersion,
            'localGrids',
          )

          projectVersion.buildings.forEach((building) => {
            building.providers
              .filter((provider) => provider.type === providerTypeToRemove)
              .forEach((providerToRemove) => {
                removeItemFromList(
                  ProviderFragmentDoc,
                  providerToRemove.id,
                  building,
                  'providers',
                )
              })
          })
        },
      })

      if (!result.data) {
        throw new Error('Local grid deletion failed')
      }

      return result.data.deleteLocalGrid
    },
  }
}
